import { IonContent, IonPage } from "@ionic/react";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import type { CallWaiting } from "@/components/communications/call-waiting";
import { IncomingCallView } from "@/components/communications/incoming-call-view";
import { IncomingStaffCallView } from "@/components/communications/incoming-staff-call-view";
import { leaveAppEntry } from "@/lib/history-entry";
import { routeTemplates } from "@/lib/routes";
import { useVoice } from "@/lib/twilio/voice-context";

export function IncomingCallPage() {
  const history = useHistory();
  const {
    activeCall,
    acceptInvite,
    endAndAcceptInvite,
    endCall,
    heldCall,
    holdSupported,
    pendingInvite,
    rejectInvite,
  } = useVoice();

  // Leave once no invite is left to answer: answered, declined, cancelled,
  // or direct navigation here. The voice context only closes the invite, so
  // this page pops it.
  useEffect(() => {
    if (
      pendingInvite ||
      history.location.pathname !== routeTemplates.app.incomingCall
    ) {
      return;
    }
    leaveAppEntry(history, "/chats");
  }, [pendingInvite, history]);

  const handleAccept = useCallback(() => {
    void acceptInvite();
  }, [acceptInvite]);
  const handleReject = useCallback(() => {
    void rejectInvite();
  }, [rejectInvite]);
  const handleEndAndAccept = useCallback(() => {
    void endAndAcceptInvite();
  }, [endAndAcceptInvite]);
  const handleHangUp = useCallback(
    (callSid: string) => {
      void endCall(callSid);
    },
    [endCall]
  );
  // Android's plugin holds no call, so there an answer leaves both live and
  // only the plain answers are offered.
  const waiting: CallWaiting | undefined = activeCall
    ? {
        choices: holdSupported
          ? { canHold: heldCall === null, onEndAndAccept: handleEndAndAccept }
          : undefined,
        heldCall,
        liveCall: activeCall,
        onHangUp: handleHangUp,
      }
    : undefined;

  // No invite (vanished mid-transition, or direct nav here): render an empty
  // page while the effect above redirects. Guarding here lets the child views
  // take a non-optional `caller`.
  if (!pendingInvite) {
    return (
      <IonPage>
        <IonContent fullscreen />
      </IonPage>
    );
  }

  // Branch on caller kind: staff-to-staff calls don't have a guest lookup
  // (no phone number → guest mapping) and shouldn't surface a reservation
  // pill.
  const isStaffInvite = pendingInvite.caller.kind === "staff";

  return (
    <IonPage>
      <IonContent fullscreen>
        {isStaffInvite ? (
          <IncomingStaffCallView
            onAccept={handleAccept}
            onReject={handleReject}
            propertyId={pendingInvite.caller.propertyId}
            staffMemberId={pendingInvite.caller.staffMemberId}
            staffName={pendingInvite.caller.staffName}
            waiting={waiting}
          />
        ) : (
          <IncomingCallView
            caller={pendingInvite.caller}
            dev={pendingInvite.dev}
            onAccept={handleAccept}
            onReject={handleReject}
            waiting={waiting}
          />
        )}
      </IonContent>
    </IonPage>
  );
}
